import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Message,
  Modal,
} from 'semantic-ui-react';
import { API, showSuccess } from '../helpers';
import './billingDiscountLifecycleV1021.css';

const prefix = 'setting.billing.lifecycle';
const icons = {
  cancel: 'cancel',
  replace: 'exchange',
  delete: 'trash',
  archive: 'archive',
  restore: 'undo',
};
const newKey = () =>
  `discount-change:${
    window.crypto?.randomUUID?.() ||
    `${Date.now()}:${Math.random().toString(16).slice(2)}`
  }`;

export const discountErrorV1021 = (error, t) => {
  const code =
    error?.response?.data?.code ||
    error?.response?.data?.message ||
    error?.message;
  if (
    code === 'discount_period_occupied' ||
    code ===
      'a discount schedule is already assigned for the selected billing period' ||
    code ===
      'a discount schedule is already assigned for the next billing cycle'
  ) {
    return t(`${prefix}.errors.discount_period_occupied`);
  }
  if (
    [
      'discount_changed',
      'discount_not_pending',
      'discount_referenced',
      'discount_unavailable',
      'discount_invalid_mutation',
      'discount_operation_failed',
    ].includes(code)
  ) {
    return t(`${prefix}.errors.${code}`);
  }
  return code || t(`${prefix}.errors.discount_operation_failed`);
};

// Keep the revision and idempotency key shown at confirmation, even if a
// background refresh changes the row. A network retry reuses that same key.
export const BillingDiscountActionV1021 = ({
  action,
  record,
  accountId,
  schedules = [],
  onChanged,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null);
  const [replacement, setReplacement] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mounted = useRef(true);
  const scope = useRef({ accountId, generation: 0 });
  if (scope.current.accountId !== accountId) {
    scope.current = { accountId, generation: scope.current.generation + 1 };
  }
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    setDraft(null);
    setError('');
    setBusy(false);
    busyRef.current = false;
  }, [accountId]);
  const name =
    draft?.record.schedule?.name ||
    draft?.record.name ||
    record.schedule?.name ||
    record.name;
  const label = t(`${prefix}.actions.${action}`);
  const close = () => {
    if (!busyRef.current) {
      setDraft(null);
      setError('');
    }
  };
  const submit = async () => {
    if (busyRef.current || !draft || (action === 'replace' && !replacement))
      return;
    busyRef.current = true;
    const generation = scope.current.generation;
    setBusy(true);
    setError('');
    const timeline = action === 'cancel' || action === 'replace';
    const url = timeline
      ? `/api/billing/v8/admin/accounts/${draft.accountId}/discount-assignments/${draft.record.id}/${action}`
      : `/api/billing/v8/admin/discount-schedules/${draft.record.id}/${action}`;
    try {
      // Keep error envelopes here; the shared interceptor otherwise consumes
      // HTTP conflicts before this confirmation can display their reason.
      const response = await API.post(
        url,
        {
          expected_revision: draft.record.revision,
          idempotency_key: draft.key,
          ...(action === 'replace' ? { schedule_id: replacement } : {}),
        },
        { validateStatus: () => true }
      );
      if (!response?.data?.success)
        throw new Error(response?.data?.code || response?.data?.message);
      if (!mounted.current || generation !== scope.current.generation) return;
      setDraft(null);
      showSuccess(t(`${prefix}.saved.${action}`));
      await onChanged();
    } catch (err) {
      if (mounted.current && generation === scope.current.generation)
        setError(discountErrorV1021(err, t));
    } finally {
      if (generation === scope.current.generation) {
        busyRef.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  };
  return (
    <>
      <Button
        type='button'
        icon
        basic
        size='small'
        disabled={disabled || busy}
        negative={action === 'cancel' || action === 'delete'}
        title={label}
        aria-label={`${label}: ${name}`}
        onClick={() => {
          setDraft({ record: { ...record }, accountId, key: newKey() });
          setReplacement('');
          setError('');
        }}
      >
        <Icon name={icons[action]} />
      </Button>
      <Modal
        open={!!draft}
        onClose={close}
        size='small'
        className='discount-modal-v1021'
        closeOnDimmerClick={!busy}
        closeOnEscape={!busy}
        closeIcon={!busy}
        aria-labelledby={`discount-${action}-${record.id}-title`}
      >
        <Modal.Header id={`discount-${action}-${record.id}-title`}>
          {label}
        </Modal.Header>
        <Modal.Content>
          <div className='discount-confirm-name-v1021'>{name}</div>
          {draft?.record.effective_period_start_iso && (
            <p>{draft.record.effective_period_start_iso}</p>
          )}
          <p>{t(`${prefix}.confirm.${action}`)}</p>
          {action === 'replace' && (
            <Form>
              <Form.Field>
                <label htmlFor={`discount-replacement-${record.id}`}>
                  {t(`${prefix}.replacement`)}
                </label>
                <Dropdown
                  id={`discount-replacement-${record.id}`}
                  aria-label={t(`${prefix}.replacement`)}
                  selection
                  search
                  fluid
                  disabled={busy}
                  value={replacement}
                  options={schedules
                    .filter(
                      (s) =>
                        s.id !== draft?.record.schedule?.id &&
                        (!s.library_state || s.library_state === 'active')
                    )
                    .map((s) => ({
                      key: s.id,
                      value: s.id,
                      text: `${s.name} v${s.version}`,
                    }))}
                  onChange={(_, { value }) => {
                    setReplacement(value);
                    setError('');
                    setDraft((current) => ({ ...current, key: newKey() }));
                  }}
                />
              </Form.Field>
            </Form>
          )}
          {error && <Message negative role='alert' content={error} />}
        </Modal.Content>
        <Modal.Actions>
          <Button type='button' onClick={close} disabled={busy}>
            {t(`${prefix}.back`)}
          </Button>
          <Button
            type='button'
            primary={
              action === 'replace' ||
              action === 'restore' ||
              action === 'archive'
            }
            negative={action === 'cancel' || action === 'delete'}
            icon
            labelPosition='left'
            onClick={submit}
            loading={busy}
            disabled={busy || (action === 'replace' && !replacement)}
          >
            <Icon name={icons[action]} />
            {t(`${prefix}.confirm_action`)}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export const loadDiscountPagesV1021 = async (url) => {
  const items = [];
  let page = 1;
  for (;;) {
    const response = await API.get(
      `${url}${url.includes('?') ? '&' : '?'}page=${page}&page_size=100`
    );
    if (!response?.data?.success)
      throw new Error(response?.data?.message || 'discount_operation_failed');
    const data = response.data.data;
    if (
      !Array.isArray(data?.items) ||
      !Number.isSafeInteger(Number(data.total)) ||
      Number(data.total) < 0
    )
      throw new Error('discount_operation_failed');
    items.push(...data.items);
    if (items.length >= Number(data.total)) return items;
    if (data.items.length === 0) throw new Error('discount_changed');
    page++;
  }
};
